function toDate(value) {
  const txt = String(value || '').trim()
  if (!txt) return null
  const parsed = new Date(txt)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed
}

function isOnOrBefore(a, b) {
  if (!a || !b) return true
  return a.getTime() <= b.getTime()
}

function hasValue(value) {
  return String(value || '').trim().length > 0
}

import { toNumber } from '../../utils/numberUtils.js'

function normalizeRegime(raw) {
  const txt = String(raw || '').trim().toUpperCase()
  if (!txt) return 'GENERAL_DEPENDENCIA'
  return txt
}

function normalizePensionType(raw) {
  const txt = String(raw || '').trim().toUpperCase()
  if (txt === 'DIRECTA') return 'DIRECTA'
  if (txt === 'DERIVADA') return 'DERIVADA'
  return ''
}

function normalizeCaseProfile(data = {}) {
  const pensionType = normalizePensionType(data?.datosPension?.tipoPension)
  if (pensionType === 'DIRECTA') return 'PENSION_DIRECTA'
  if (pensionType === 'DERIVADA') return 'PENSION_DERIVADA'

  const regime = normalizeRegime(data?.regimeType || data?.regimen || data?.caseType)
  if (regime === 'AUTONOMO_PURO') return 'AUTONOMO_PURO'
  if (regime === 'MIXTO') return 'MIXTO'
  if (regime === 'GENERAL_DEPENDENCIA') return 'GENERAL_DEPENDENCIA'
  if (regime.startsWith('ESPECIAL_')) return regime

  if (data?.esDocente === true) return 'ESPECIAL_DOCENTE'
  return 'GENERAL_DEPENDENCIA'
}

function getDerivedStrategy(profile) {
  switch (profile) {
    case 'PENSION_DIRECTA':
      return {
        indice: 'POR_FECHA_FALLECIMIENTO',
        lockUserChoice: true,
        reason: 'Pension directa: la estrategia depende de fecha de fallecimiento y regularidad.',
      }
    case 'PENSION_DERIVADA':
      return {
        indice: 'NO_RECALCULA_BASE',
        lockUserChoice: true,
        reason: 'Pension derivada: no corresponde recalculo de base general.',
      }
    case 'ESPECIAL_DOCENTE':
      return {
        indice: 'RIPDOC',
        lockUserChoice: true,
        reason: 'Regimen especial docente: movilidad sectorial protegida.',
      }
    case 'ESPECIAL_LUZ_Y_FUERZA':
      return {
        indice: 'MOVILIDAD_SECTORIAL',
        lockUserChoice: true,
        reason: 'Regimen especial sectorial: usar movilidad especifica del regimen.',
      }
    case 'ESPECIAL_FUERZAS_SEGURIDAD':
      return {
        indice: 'MOVILIDAD_SECTORIAL',
        lockUserChoice: true,
        reason: 'Regimen especial de fuerzas: usar movilidad especifica del regimen.',
      }
    case 'AUTONOMO_PURO':
      return {
        indice: 'RIPTE',
        lockUserChoice: false,
        reason: 'Autonomo puro: estrategia base sugerida RIPTE salvo regla activa distinta.',
      }
    case 'MIXTO':
      return {
        indice: 'POR_FECHA_CESE_MIXTO',
        lockUserChoice: false,
        reason: 'Caso mixto: estrategia dependiente de composicion contributiva.',
      }
    case 'GENERAL_DEPENDENCIA':
    default:
      return {
        indice: 'POR_FECHA_CESE',
        lockUserChoice: false,
        reason: 'Regimen general: estrategia por fecha de cese.',
      }
  }
}

function validateTemporalRules(data, profile, blockingIssues) {
  const fechaAlta = toDate(data?.fechaOtorgamientoBeneficio || data?.fechaAdquisicion)
  const fechaCese = toDate(data?.fechaCese)
  const fechaCorte = toDate(data?.fechaCorteCalculo)
  const fechaReclamo = toDate(data?.fechaReclamoAdministrativo)
  const fechaDemanda = toDate(data?.fechaDenegatoriaODemanda)
  const fechaFallecimiento = toDate(data?.datosPension?.fechaFallecimiento)

  if (hasValue(data?.fechaOtorgamientoBeneficio || data?.fechaAdquisicion) && !fechaAlta) {
    blockingIssues.push('Fecha de alta/adquisicion invalida: usar formato de fecha valido.')
  }
  if (hasValue(data?.fechaCese) && !fechaCese) {
    blockingIssues.push('Fecha de cese invalida: verificar formato y valor legal del cese real.')
  }
  if (hasValue(data?.fechaCorteCalculo) && !fechaCorte) {
    blockingIssues.push('Fecha de corte invalida: verificar formato de fecha pericial.')
  }
  if (hasValue(data?.fechaReclamoAdministrativo) && !fechaReclamo) {
    blockingIssues.push('Fecha de reclamo administrativo invalida.')
  }
  if (hasValue(data?.fechaDenegatoriaODemanda) && !fechaDemanda) {
    blockingIssues.push('Fecha de denegatoria o demanda invalida.')
  }

  if (fechaAlta && fechaCese && !isOnOrBefore(fechaAlta, fechaCese)) {
    blockingIssues.push('Incompatibilidad temporal: la fecha de alta/adquisicion no puede ser posterior a la fecha de cese.')
  }
  if (fechaCese && fechaCorte && !isOnOrBefore(fechaCese, fechaCorte)) {
    blockingIssues.push('Incompatibilidad temporal: la fecha de cese no puede ser posterior a la fecha de corte del calculo.')
  }
  if (fechaReclamo && fechaDemanda && !isOnOrBefore(fechaReclamo, fechaDemanda)) {
    blockingIssues.push('Incompatibilidad temporal: el reclamo administrativo no puede ser posterior a la denegatoria o demanda.')
  }

  if (profile === 'PENSION_DIRECTA' || profile === 'PENSION_DERIVADA') {
    if (!hasValue(data?.datosPension?.fechaFallecimiento)) {
      blockingIssues.push('Pension: la fecha de fallecimiento es obligatoria para definir procedencia y estrategia de calculo.')
    } else if (!fechaFallecimiento) {
      blockingIssues.push('Pension: la fecha de fallecimiento informada es invalida.')
    }
  }

  if (profile === 'PENSION_DERIVADA' && toNumber(data?.datosPension?.haberJubiladoCausante) <= 0) {
    blockingIssues.push('Pension derivada: debe informarse haber causante mayor a cero.')
  }
}

function validateUniversalCompatibility(data, profile, blockingIssues, warnings) {
  const hasPension = profile === 'PENSION_DIRECTA' || profile === 'PENSION_DERIVADA'
  const hasSpecial = profile.startsWith('ESPECIAL_')
  const hasDocente = profile === 'ESPECIAL_DOCENTE'

  if (hasPension && hasSpecial) {
    blockingIssues.push('Configuracion incompatible: no combinar regimen especial con contingencia pension en el mismo flujo base.')
  }

  if (hasDocente) {
    const sueldoDocente = toNumber(data?.datosDocentes?.sueldoBrutoCargoCese)
    const mesesContinuos = toNumber(data?.datosDocentes?.mesesEnCargoContinuos)
    const mesesAlternados = toNumber(data?.datosDocentes?.mesesEnCargoAlternados)
    const continuidadOk = mesesContinuos >= 60 || mesesAlternados >= 120
    if (!(sueldoDocente > 0)) {
      blockingIssues.push('Regimen especial docente: falta sueldo bruto del cargo al cese/corte.')
    }
    if (!continuidadOk) {
      blockingIssues.push('Regimen especial docente: se requiere continuidad minima (60 meses continuos o 120 alternados).')
    }

    const haberActual = toNumber(data?.haberActual)
    if (haberActual > 0 && sueldoDocente > 0) {
      const ratio = haberActual / sueldoDocente
      if (ratio >= 3) {
        warnings.push('Caso complejo: la escala entre haber actual y sueldo docente es divergente; revisar composicion docente/civil antes de calcular.')
      }
    }
  }

  if (hasPension && hasValue(data?.fechaCese)) {
    warnings.push('Pension: la fecha de cese general puede no ser determinante; validar prioridad de fecha de fallecimiento.')
  }

  if (!hasValue(data?.haberActual)) {
    warnings.push('No se informo haber mensual actual: el diferencial economico puede degradarse o perder trazabilidad pericial.')
  }
}

export function validateCaseParameters(data = {}) {
  const blockingIssues = []
  const warnings = []
  const profile = normalizeCaseProfile(data)
  const derivedStrategy = getDerivedStrategy(profile)

  validateTemporalRules(data, profile, blockingIssues)
  validateUniversalCompatibility(data, profile, blockingIssues, warnings)

  if (derivedStrategy.lockUserChoice) {
    const userSelected = String(data?.fallo || data?.indiceSeleccionado || '').trim().toUpperCase()
    const forced = String(derivedStrategy.indice || '').trim().toUpperCase()
    if (userSelected && forced && userSelected !== forced) {
      warnings.push(`El indice seleccionado por el usuario sera reemplazado por estrategia obligatoria: ${derivedStrategy.indice}.`)
    }
  }

  return {
    isValid: blockingIssues.length === 0,
    blockingIssues,
    warnings,
    derivedStrategy: {
      ...derivedStrategy,
      caseProfile: profile,
    },
  }
}

export default validateCaseParameters
